import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAppContext } from '../contexts/AppContext';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, LogOut, User, QrCode, Calendar, Activity, Users, BarChart3, ChevronDown, DollarSign } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import logo from '../assets/brain-imaginem.png';

export default function MobileLayout({ children }) {
  const { user, logout } = useAuth();
  const { moduleTitle, globalStats } = useAppContext();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobilePairingUrl, setMobilePairingUrl] = useState('');

  useEffect(() => {
    if (!userMenuOpen || !globalStats?.stationId) return;
    fetch('/api/mobile/pairing', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ station_id: globalStats.stationId, device_type: 'scan' })
    })
      .then((response) => response.ok ? response.json() : Promise.reject(new Error('pairing_failed')))
      .then((data) => setMobilePairingUrl(data.url))
      .catch(() => setMobilePairingUrl(''));
  }, [userMenuOpen, globalStats?.stationId]);

  const modules = [
    { title: 'Home', path: '/', icon: <Activity className="w-5 h-5 text-indigo-600" />, roles: ['admin', 'recepcion', 'patient_care', 'hr', 'dev', 'crm', 'marketing'] },
    { title: 'Agendamiento', path: '/agendamiento', icon: <Calendar className="w-5 h-5 text-indigo-600" />, roles: ['admin', 'agendamiento', 'patient_care'] },
    { title: 'Recepción', path: '/finanzas/recepcion', icon: <Calendar className="w-5 h-5 text-blue-500" />, roles: ['admin', 'recepcion', 'patient_care'] },
    { title: 'Finanzas Corp.', path: '/finanzas/corporativas', icon: <DollarSign className="w-5 h-5 text-emerald-600" />, roles: ['admin', 'finance'] },
    { title: 'BI Pacientes', path: '/pacientes/bi', icon: <BarChart3 className="w-5 h-5 text-indigo-500" />, roles: ['admin', 'patient_care'] },
    { title: 'Operaciones Médicas', path: '/operaciones', icon: <Activity className="w-5 h-5 text-amber-500" />, roles: ['admin', 'operaciones', 'patient_care', 'tecnico', 'medico', 'dev'] },
    { title: 'Recursos Humanos', path: '/hr', icon: <Users className="w-5 h-5 text-emerald-500" />, roles: ['admin', 'hr'] },
    { title: 'CRM de Médicos', path: '/crm', icon: <Users className="w-5 h-5 text-blue-600" />, roles: ['admin', 'crm', 'canvaseador', 'closer'] },
    { title: 'Marketing Masivo', path: '/marketing', icon: <BarChart3 className="w-5 h-5 text-purple-600" />, roles: ['admin', 'marketing'] },
  ];

  const userRoles = Array.isArray(user?.roles) ? user.roles : [];
  const accessibleModules = modules.filter(m => 
    user && Array.isArray(user.roles) && Array.isArray(m.roles) && m.roles.some(r => userRoles.includes(r))
  );

  const hasRecepcionAccess = user && Array.isArray(user.roles) && (userRoles.includes('admin') || userRoles.includes('recepcion') || userRoles.includes('patient_care'));

  return (
    <div className="flex flex-col h-screen w-full bg-slate-50 overflow-hidden font-sans">
      {/* Mobile Top Header */}
      <header className="h-14 bg-white border-b border-slate-200 px-4 flex items-center justify-between shrink-0 shadow-sm z-40 relative">
        <button 
          onClick={() => setMenuOpen(!menuOpen)}
          className="p-1.5 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
        >
          {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>

        <div className="flex items-center gap-2 flex-1 justify-center overflow-hidden px-2">
          <img src={logo} alt="Imaginem Logo" className="w-7 h-7 object-contain shrink-0" />
          <span className="text-sm font-black text-slate-800 truncate">{moduleTitle}</span>
        </div>

        <div className="relative">
          <button 
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="flex items-center gap-1 p-1 rounded-full hover:bg-slate-100 transition"
          >
            <div className="w-8 h-8 rounded-full overflow-hidden bg-gradient-to-tr from-indigo-500 to-purple-600 p-[2px] shadow-sm">
              <div className="w-full h-full rounded-full overflow-hidden bg-white flex items-center justify-center font-bold text-indigo-600 text-xs">
                {user?.foto_perfil ? (
                  <img src={user.foto_perfil} alt={user.nombre || user.email} className="w-full h-full object-cover" />
                ) : (
                  (user?.nombre || user?.email || 'U').charAt(0).toUpperCase()
                )}
              </div>
            </div>
            <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
          </button>

          {/* User Dropdown */}
          {userMenuOpen && (
            <div className="absolute right-0 top-11 w-64 bg-white rounded-2xl shadow-xl border border-slate-200 py-3 z-50 animate-in fade-in zoom-in-95">
              <div className="px-4 py-2 border-b border-slate-100 mb-2">
                <p className="text-xs font-black text-slate-800 truncate">{user?.nombre || user?.email}</p>
                <p className="text-[10px] font-bold text-emerald-600 flex items-center gap-1 mt-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                  Sesión Activa
                </p>
              </div>

              <button 
                onClick={() => { setUserMenuOpen(false); alert('Mis Datos: ' + (user?.email || '')); }}
                className="w-full px-4 py-2 text-left text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-2"
              >
                <User className="w-4 h-4 text-indigo-500" />
                Mis Datos
              </button>

              {hasRecepcionAccess && globalStats?.stationId && (
                <div className="px-4 py-3 border-t border-b border-slate-100 my-2 bg-slate-50/50 flex flex-col items-center">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Emparejar Celular</p>
                  <div className="bg-white p-2 border border-slate-200 rounded-xl shadow-sm">
                    {mobilePairingUrl ? <QRCodeCanvas value={mobilePairingUrl} size={120} level={"L"} /> : <div className="w-[120px] h-[120px] flex items-center justify-center text-xs text-slate-400">Generando vínculo…</div>}
                  </div>
                  <span className="text-[10px] font-bold text-indigo-600 mt-1">Escanea para subir docs</span>
                </div>
              )}

              <button 
                onClick={() => { setUserMenuOpen(false); logout(); }}
                className="w-full px-4 py-2 text-left text-xs font-bold text-red-600 hover:bg-red-50 flex items-center gap-2"
              >
                <LogOut className="w-4 h-4 text-red-500" />
                Cerrar Sesión
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Mobile Drawer Menu */}
      {menuOpen && (
        <div className="fixed inset-0 bg-slate-900/50 z-50 flex animate-in fade-in">
          <div className="w-72 bg-white h-full shadow-2xl flex flex-col p-4 animate-in slide-in-from-left duration-200">
            <div className="flex items-center justify-between border-b border-slate-200 pb-4 mb-4">
              <div className="flex items-center gap-2">
                <img src={logo} alt="Logo" className="w-8 h-8 object-contain" />
                <span className="font-bold text-slate-800 text-base">GEMA ERP</span>
              </div>
              <button onClick={() => setMenuOpen(false)} className="p-1 text-slate-400 hover:text-slate-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3">Módulos</p>
            <div className="flex flex-col gap-1 overflow-y-auto flex-1">
              {accessibleModules.map((m, idx) => (
                <button 
                  key={idx}
                  onClick={() => { setMenuOpen(false); navigate(m.path); }}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-bold text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors text-left"
                >
                  {m.icon}
                  {m.title}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1" onClick={() => setMenuOpen(false)}></div>
        </div>
      )}

      {/* Mobile Main Content */}
      <main className="flex-1 overflow-y-auto w-full bg-slate-50 relative flex flex-col">
        {children}
      </main>

      {/* Mobile Bottom Global Status Bar */}
      <footer className="h-10 bg-white border-t border-slate-200 px-3 flex items-center justify-between shrink-0 shadow-lg text-[10px] font-bold text-slate-600 overflow-x-auto gap-4 scrollbar-none">
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-slate-400 uppercase tracking-wider">Progreso:</span>
          <span className="text-emerald-600 font-black">{globalStats?.progressPct || 0}%</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-slate-400 uppercase tracking-wider">Agendados:</span>
          <span className="text-slate-800 font-black">{globalStats?.totalPacientes || 0}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-slate-400 uppercase tracking-wider">Atendidos:</span>
          <span className="text-emerald-700 font-black">{globalStats?.totalAtendidos || 0}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-slate-400 uppercase tracking-wider">Registro:</span>
          <span className="text-amber-500 font-black">{globalStats?.enRegistro || 0}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-slate-400 uppercase tracking-wider">Espera:</span>
          <span className="text-indigo-600 font-black">{globalStats?.enEspera || 0}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-slate-400 uppercase tracking-wider">Estudio:</span>
          <span className="text-purple-600 font-black">{globalStats?.enEstudio || 0}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-slate-400 uppercase tracking-wider">Fin:</span>
          <span className="text-emerald-600 font-black">{globalStats?.finalizados || 0}</span>
        </div>
      </footer>
    </div>
  );
}
